import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  sendChatMessageStream,
  initChat,
  getPersistedConversationId,
  setPersistedConversationId,
  clearPersistedConversationId,
} from '../api/chatClient';
import type { StreamEvent, FeedbackRating } from '../api/chatClient';
import { useAuth } from '../auth/useAuth';
import { RuntimeConfigContext } from '../config/runtimeConfigContext';
import { rewardsExperience } from '../config/rewardsExperience';
import { extractSuggestedPrompts, resolveCitations, stripResolvedTag, stripSignalsForDisplay } from '../utils/chatSignals';
import type { Citation } from '../api/chatClient';
// Rewards is an AI-only surface (no Omnichannel, no live agent). These two are retained for history
// rehydration only: restoreLiveHistory de-duplicates replayed turns by message id via LiveMessageGate.
import { LiveMessageGate, restoreLiveHistory, sourceMessageId } from '../../../../../../partner/widget/src/liveMessages';
import { CaseDetailsMessage, readCaseDetails } from '../../../../../../partner/widget/src/CaseDetailsMessage';
import { EN_STRINGS } from '../../../../../../partner/widget/src/strings';
import type { CmspCaseDetails } from '../../../../../../partner/widget/src/contract';

type Role = 'user' | 'agent' | 'system';
type FeedbackReason = 'new-chat';

interface Message {
  caseDetails?: CmspCaseDetails;
  clientOperationId?: string;
  messageId?: string;
  id: string;
  role: Role;
  text: string;
  senderName?: string;
  streaming?: boolean;
  sentAt?: number;
  receivedAt?: number;
  /** AI turn: choice pills parsed from a `[SUGGESTIONS: a | b | c]` signal. */
  suggestedPrompts?: string[];
  /** AI turn: citations parsed from a model-written "Sources" block (rare; we render inline links). */
  citations?: Citation[];
  /** AI turn: server turn id. Feedback is attached to THIS, not to the local message id. */
  turnId?: string;
  /** AI turn: server says this turn was KB-grounded, so rating it is meaningful. Gates the thumbs. */
  feedbackEligible?: boolean;
  /** AI turn: the rating already recorded for this turn, or null when none/cleared. */
  feedback?: FeedbackRating | null;
  /** AI turn: guards against double-submitting while a rating request is in flight. */
  isFeedbackProcessing?: boolean;
  /**
   * AI turn: this bubble is the acknowledgement of a slash command, not a real answer, so it gets no
   * feedback bar. A thumbs-down that REGENERATES is exempt — it produces a genuine, rateable answer.
   */
  isSlashCommandResponse?: boolean;
}

// Suggested prompts shown in the hero and on the empty chat panel. Sourced from the rewards config
// so copy stays in one place. label === prompt for these (they read as natural questions).
const QUICK_PROMPTS = rewardsExperience.quickPrompts.map(p => ({ label: p, prompt: p }));

// Transport/stream failures can carry a raw HTTP body — e.g. an Azure App Service
// "Error 403 - Forbidden" HTML page when the backend is down, restarting, or access-restricted.
// Never surface that verbatim in the chat: map it to a short, safe, human sentence.
function friendlyStreamError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  const status = raw.match(/\b(\d{3})\b/)?.[1];
  const looksLikeHtml = /<!doctype|<html|<\/?[a-z][\s\S]*>/i.test(raw);
  const unavailable = 'The support service is temporarily unavailable. Please try again in a moment.';
  switch (status) {
    case '401':
    case '403':
    case '500':
    case '502':
    case '503':
    case '504':
      return unavailable;
    case '408':
    case '429':
      return 'The support service is busy right now. Please wait a moment and try again.';
    default:
      // Opaque, oversized, or HTML/markup bodies must never reach the user verbatim.
      if (!raw || looksLikeHtml || raw.length > 160) {
        return 'Something went wrong. Please try again in a moment.';
      }
      return raw;
  }
}

/** Map IANA timezone to coarse geographic region hint for the API. */
function deriveCoarseRegion(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  if (tz.startsWith('America/')) return 'us';
  if (tz.startsWith('Europe/')) return 'eu';
  if (tz.startsWith('Asia/') || tz.startsWith('Australia/') || tz.startsWith('Pacific/')) return 'apac';
  return 'unknown';
}

/** Normalize a URL locale like "en-us" to the BCP-47 "en-US" the API expects (region uppercased). */
function normalizeLocale(loc?: string): string {
  const m = (loc ?? '').match(/^([a-z]{2})-([a-z]{2})$/i);
  return m ? `${m[1].toLowerCase()}-${m[2].toUpperCase()}` : (loc || 'en-US');
}

// Partitions this page's persisted conversation id away from the portal ChatWidget's. Both render in
// the same origin and the rewards route is a same-document navigation, so an unscoped key would let
// one surface resume (and rehydrate the transcript of) a conversation started by the other.
const CONVERSATION_SCOPE = 'rewards';

// Upper bound on how long a send will wait for MSAL to settle before initializing anyway. Long enough
// to cover a loginRedirect round-trip completing, short enough that a wedged MSAL never blocks chat.
const AUTH_SETTLE_TIMEOUT_MS = 5_000;
const AUTH_SETTLE_POLL_MS = 50;

function canRestoreFocus(element: HTMLElement | null): element is HTMLElement {
  return !!element?.isConnected && !element.closest('[hidden], [inert]')
    && !element.matches(':disabled');
}

function tabStops(dialog: HTMLDialogElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(
    'a[href], button, input, textarea, select, summary, [tabindex], [contenteditable="true"]',
  )).filter(element => {
    if (element.tabIndex < 0 || element.matches(':disabled')
      || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    for (let parent: HTMLElement | null = element; parent; parent = parent.parentElement) {
      const style = getComputedStyle(parent);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
      if (parent instanceof HTMLDetailsElement && !parent.open
        && !parent.querySelector('summary')?.contains(element)) return false;
    }
    return true;
  });
}

// Native modal dialogs isolate the background without unmounting the conversation.
function useRewardsDialog(
  dialogRef: React.RefObject<HTMLDialogElement | null>,
  open: boolean,
  initialFocusRef: React.RefObject<HTMLElement | null>,
  options: {
    openerRef?: React.RefObject<HTMLElement | null>;
    lockBody?: boolean;
  } = {},
) {
  const { openerRef, lockBody = false } = options;
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const opener = openerRef?.current
      ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const parent = dialog.parentElement?.closest('dialog');
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    if (lockBody) document.body.style.overflow = 'hidden';
    const target = initialFocusRef.current;
    if (target && !target.matches(':disabled')) {
      target.focus();
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } else {
      dialog.focus();
    }
    const wrapTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented
        || !(event.target instanceof Element) || event.target.closest('dialog') !== dialog) return;
      const stops = tabStops(dialog);
      const current = document.activeElement instanceof HTMLElement ? stops.indexOf(document.activeElement) : -1;
      // A revealed region may take programmatic focus; let Tab enter its content naturally.
      if (current < 0 && document.activeElement !== dialog) return;
      if (current < 0 || (event.shiftKey ? current === 0 : current === stops.length - 1)) {
        event.preventDefault();
        const target = event.shiftKey ? stops.at(-1) : stops[0];
        (target ?? dialog).focus();
        target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };
    dialog.addEventListener('keydown', wrapTab);
    return () => {
      dialog.removeEventListener('keydown', wrapTab);
      dialog.close();
      if (lockBody) document.body.style.overflow = previousOverflow;
      // React removes hidden/inert state during the commit before restoring the real opener.
      queueMicrotask(() => {
        if (canRestoreFocus(opener)) opener.focus({ preventScroll: true });
        else if (parent?.open) parent.focus({ preventScroll: true });
      });
    };
  }, [dialogRef, initialFocusRef, open, openerRef, lockBody]);
}

export function RewardsSupportPage({ locale = 'en-us' }: { locale?: string } = {}) {
  const { account, isSignedIn, isAuthPending, puid, tenantId, acquireApiToken, signIn } = useAuth();
  const cfg = useContext(RuntimeConfigContext);
  // A locale-prefixed URL (e.g. /en-us/rewards) drives the chat's Accept-Language + escalation locale.
  const requestLocale = normalizeLocale(locale);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'agent',
      text: rewardsExperience.welcomeMessage,
    },
  ]);
  const [draft, setDraft] = useState('');
  const [heroDraft, setHeroDraft] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [pending, setPending] = useState(false);
  // Seeded from storage so a refresh resumes the SAME server conversation (and can rehydrate its
  // history via /chat/init) instead of silently starting a new one on the next stream turn.
  const [conversationId, setConversationId] = useState<string | undefined>(
    () => getPersistedConversationId(CONVERSATION_SCOPE) ?? undefined,
  );
  const [initChatDone, setInitChatDone] = useState(false);
  // De-dupes concurrent init calls (open effect + first send racing) onto one in-flight request.
  const initChatPromiseRef = useRef<Promise<string | undefined> | null>(null);
  // Mirrors isAuthPending for the async send path, which cannot read the latest render's value.
  const authPendingRef = useRef(isAuthPending);
  // Live identity snapshot. callInitChat may await MSAL settling, after which everything it captured
  // in its closure (isSignedIn, account, puid, tenantId, acquireApiToken — which itself closes over
  // account) is one render stale and still reports signed-out. Reading identity from this ref AFTER
  // the wait is what makes the gate actually worth waiting for.
  const authRef = useRef({ isSignedIn, account, puid, tenantId, acquireApiToken });
  const abortRef = useRef<AbortController | null>(null);
  // Bumped whenever the signed-in identity changes or the chat is reset, so an in-flight async
  // continuation can tell it no longer owns the conversation and must not write to it.
  const turnGenerationRef = useRef(0);
  const ownerRef = useRef(account?.homeAccountId);
  ownerRef.current = account?.homeAccountId;
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const newChatRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDialogElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const reasonOpenerRef = useRef<HTMLElement | null>(null);
  const panelOpenRef = useRef(panelOpen);
  panelOpenRef.current = panelOpen;
  const messagesRef = useRef<Message[]>([]);
  const liveMessageGateRef = useRef(new LiveMessageGate());
  // Latest conversation id reachable from callbacks (e.g. resetChat) without re-creating them per change.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const [feedbackReason, setFeedbackReason] = useState<FeedbackReason | null>(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  // Consecutive thumbs-down count for the CURRENT question. The first two regenerate the answer; the
  // third opens the reason dialog. Reset by any new user-initiated turn (parity with the widget).
  const downCountRef = useRef(0);
  const [reasonDialog, setReasonDialog] = useState<{ messageId: string; turnId: string } | null>(null);
  // Lets callbacks declared ABOVE `send` (completeFeedback's CSAT submit) reach it.
  const sendRef = useRef<((text?: string, opts?: { silent?: boolean }) => Promise<void>) | null>(null);
  // Tracks the message whose text was just copied, to briefly swap the copy icon for a check.
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const copiedResetRef = useRef<number | null>(null);

  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { authPendingRef.current = isAuthPending; }, [isAuthPending]);
  useEffect(() => {
    authRef.current = { isSignedIn, account, puid, tenantId, acquireApiToken };
  }, [isSignedIn, account, puid, tenantId, acquireApiToken]);

  // Inject animation keyframes once for the typing indicator.
  useEffect(() => {
    const styleId = 'cmsp-rewards-activity-style';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes cmsp-chat-bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-8px)}}
      .rewards-support-input:focus-visible{outline:2px solid ${rewardsTheme.accent};outline-offset:2px}
      .rewards-chat-dialog::backdrop{background:rgba(0,0,0,0.28)}
      .rewards-reason-dialog::backdrop{background:rgba(0,0,0,0.35)}
      .rewards-chat-dialog :where(button,a,input,textarea):focus-visible{outline:2px solid ${rewardsTheme.link};outline-offset:2px}
      .rewards-chat-header button:focus-visible{outline-color:white}
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const prevFeedbackReasonRef = useRef<FeedbackReason | null>(null);
  useEffect(() => {
    const previous = prevFeedbackReasonRef.current;
    prevFeedbackReasonRef.current = feedbackReason;
    if (previous && !feedbackReason && panelOpen) {
      newChatRef.current?.focus({ preventScroll: true });
    }
  }, [feedbackReason, panelOpen]);

  useEffect(() => {
    return () => {
      turnGenerationRef.current += 1;
      abortRef.current?.abort();
    };
  }, []);

  const resetChat = useCallback(() => {
    turnGenerationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    liveMessageGateRef.current.reset();
    setMessages([{
      id: 'welcome',
      role: 'agent',
      text: rewardsExperience.welcomeMessage,
    }]);
    setDraft('');
    setPending(false);
    conversationIdRef.current = undefined;
    setConversationId(undefined);
    // Drop the stored id too, otherwise the next refresh would resume the conversation the customer
    // just chose to abandon (and rehydrate its history over the fresh welcome message).
    clearPersistedConversationId(CONVERSATION_SCOPE);
    setInitChatDone(false);
    initChatPromiseRef.current = null;
    setFeedbackReason(null);
    setFeedbackRating(0);
    if (panelOpenRef.current) inputRef.current?.focus();
  }, []);

  // A different signed-in account must never continue the previous account's conversation: the
  // server caches session identity per conversation, so resuming would attribute this customer's
  // turns to the previous identity.
  const accountRef = useRef(account?.homeAccountId);
  useEffect(() => {
    if (accountRef.current !== account?.homeAccountId) {
      accountRef.current = account?.homeAccountId;
      if (conversationIdRef.current) resetChat();
    }
  }, [account?.homeAccountId, resetChat]);

  const requestNewChat = useCallback(() => {
    const hasConversation = messages.some(message => message.id !== 'welcome');
    if (hasConversation) {
      setFeedbackReason('new-chat');
      setFeedbackRating(0);
      return;
    }

    resetChat();
  }, [messages, resetChat]);

  const completeFeedback = useCallback((rating?: number) => {
    if (rating) setFeedbackRating(rating);
    const shouldStartNewChat = feedbackReason === 'new-chat';
    const score = rating ?? feedbackRating;

    // Submit BEFORE any reset: the score belongs to the conversation being rated, and resetChat
    // clears the conversation id the turn needs. Routed through a ref because `send` is declared
    // later in this component. Fire-and-forget — the card has already thanked the customer, and a
    // failed rating must never block starting a new chat.
    if (score >= 1 && score <= 5) {
      void sendRef.current?.(`/csat score=${score}`, { silent: true });
    }

    setFeedbackReason(null);
    setFeedbackRating(0);

    if (shouldStartNewChat) {
      resetChat();
    }
  }, [feedbackReason, feedbackRating, resetChat]);

  const handleCopy = useCallback((messageId: string) => {
    const msg = messagesRef.current.find(m => m.id === messageId);
    if (!msg) return;
    const text = (msg.text ?? '').trim();
    if (!text) return;
    const markCopied = () => {
      setCopiedMsgId(messageId);
      if (copiedResetRef.current) window.clearTimeout(copiedResetRef.current);
      copiedResetRef.current = window.setTimeout(() => setCopiedMsgId(null), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(() => {
        // eslint-disable-next-line no-console
        console.error('Copy to clipboard failed');
      });
    } else {
      // Fallback for insecure contexts / older browsers without the async clipboard API.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        markCopied();
      } catch {
        // eslint-disable-next-line no-console
        console.error('Copy to clipboard failed');
      }
    }
  }, []);

  useEffect(() => () => {
    if (copiedResetRef.current) window.clearTimeout(copiedResetRef.current);
  }, []);

  // Resolves once MSAL has finished starting up (or the cap elapses).
  //
  // This gate is the whole reason init is deferred: the server caches session identity once per
  // conversation and this page has no mid-conversation upgrade path, so an init fired while MSAL is
  // still processing a loginRedirect would stamp the conversation anonymous PERMANENTLY — and the id
  // is now persisted, so that mis-stamp would survive refreshes. Downstream that yields a support case
  // with no PUID. The open effect gates on isAuthPending declaratively; the send path cannot (it runs
  // async, after the render that read the flag), so it awaits this instead.
  const waitForAuthSettled = useCallback(async (): Promise<void> => {
    if (!authPendingRef.current) return;
    const deadline = Date.now() + AUTH_SETTLE_TIMEOUT_MS;
    while (authPendingRef.current && Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, AUTH_SETTLE_POLL_MS));
    }
  }, []);

  // Establishes (or resumes) the chat session BEFORE the first stream turn — the same
  // POST /api/v1/chat/init the front-door ChatWidget calls when its window opens.
  //
  // This is not optional bookkeeping: the server caches the session identity once per conversation, so
  // whichever call creates the conversation decides whether it is anonymous or signed-in for its whole
  // lifetime. Letting /chat/stream create it implicitly (what this page used to do) also means the
  // route greeting, prior history, live-session resume and agent introduction are never retrieved.
  //
  // Returns the conversation id so a racing first send can use it without waiting for React state.
  const callInitChat = useCallback(async (): Promise<string | undefined> => {
    if (initChatPromiseRef.current) {
      return initChatPromiseRef.current;
    }

    const initialize = async (): Promise<string | undefined> => {
      try {
        await waitForAuthSettled();
        // Read identity AFTER the wait — the closure's copy predates MSAL settling (see authRef).
        const auth = authRef.current;
        // Bearer is optional here: ApiAuthMiddleware trusts bearer-less same-origin SPA requests that
        // send no identity headers, which is what an anonymous Rewards visitor is (ADR-0013).
        const bearer = auth.isSignedIn ? await auth.acquireApiToken().catch(() => null) : null;
        // Resume the persisted conversation if there is one; otherwise init issues the id.
        const convId = conversationId ?? getPersistedConversationId(CONVERSATION_SCOPE) ?? undefined;

        const response = await initChat(
          {
            conversationId: convId,
            sourceAppName: 'Microsoft Rewards Support',
            callerAppId: cfg?.clientId ?? 'unknown',
            locale: requestLocale,
            region: deriveCoarseRegion(),
            isAuthenticated: auth.isSignedIn,
            homeAccountId: auth.account?.homeAccountId,
            puid: auth.puid,
            tenantId: auth.tenantId,
            email: auth.account?.username,
            displayName: auth.account?.name,
            entryPoint: rewardsExperience.entryPoint,
          },
          bearer,
          { persistenceScope: CONVERSATION_SCOPE },
        );

        conversationIdRef.current = response.conversationId;
        setConversationId(response.conversationId);
        setPersistedConversationId(response.conversationId, CONVERSATION_SCOPE);
        setInitChatDone(true);

        // Resume-on-refresh. Only replace the transcript while it is still the untouched welcome
        // seed — never clobber messages the customer has already accumulated this session.
        if (!response.isNew && response.history && response.history.length > 0 && !messagesRef.current.some(m => m.id !== 'welcome')) {
          const restored = restoreLiveHistory(liveMessageGateRef.current, response.history);
          setMessages(prev => {
            if (prev.some(m => m.id !== 'welcome')) {
              return prev;
            }
            return restored.messages.map(h => {
              const identity = { id: sourceMessageId(h.messageId) ?? crypto.randomUUID(), messageId: sourceMessageId(h.messageId) };
              if (h.role === 'user') {
                return { ...identity, role: 'user' as const, text: h.text };
              }
              if (h.role === 'system') {
                return { ...identity, role: 'system' as const, text: h.text };
              }
              // Agent turn: apply the SAME signal cleanup the live onDone handler does, so a resumed
              // bubble renders identically to the one the customer originally saw.
              const resolved = resolveCitations(h.citations ?? undefined, h.text);
              const extracted = extractSuggestedPrompts(resolved.text);
              return {
                ...identity,
                role: 'agent' as const,
                text: stripResolvedTag(extracted.text).text,
                caseDetails: readCaseDetails(h.caseDetails),
                suggestedPrompts:
                  extracted.suggestedPrompts.length > 0 ? extracted.suggestedPrompts : undefined,
                citations: resolved.citations.length > 0 ? resolved.citations : undefined,
                // Restore the feedback bar and any rating already given, so a refresh does not
                // present an un-rated turn the customer has in fact already rated.
                turnId: h.turnId ?? undefined,
                feedbackEligible: h.feedbackEligible ?? undefined,
                feedback: h.feedback ?? null,
              };
            });
          });
        }

        // The route's GreetingMessage is server-owned and locale-aware, so it supersedes the bundled
        // welcome copy. Replacing (not appending) avoids showing two greetings back to back; the
        // bundled text stays as the pre-init placeholder and the offline fallback.
        if (response.isNew && response.greeting) {
          setMessages(prev =>
            prev.length === 1 && prev[0].id === 'welcome'
              ? [{ id: 'welcome', role: 'agent', text: response.greeting! }]
              : prev,
          );
        }

        return response.conversationId;
      } catch (err) {
        // Non-fatal: the send path still streams (the server creates the conversation implicitly), the
        // customer just loses greeting/history/resume for this turn.
        // eslint-disable-next-line no-console
        console.error('[rewardsSupportPage] initChat failed:', err);
        return undefined;
      }
    };

    const promise = initialize();
    initChatPromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      if (initChatPromiseRef.current === promise) {
        initChatPromiseRef.current = null;
      }
    }
  }, [cfg, conversationId, requestLocale, waitForAuthSettled]);

  // Initialize when the chat panel opens.
  //
  // Gated on isAuthPending: on a page load with a cached MSAL session, MSAL briefly reports no account.
  // An init fired in that window would stamp the conversation anonymous PERMANENTLY, because the server
  // caches session identity once per conversation and this page has no mid-conversation upgrade path.
  useEffect(() => {
    if (panelOpen && !initChatDone && !isAuthPending) {
      void callInitChat();
    }
  }, [panelOpen, initChatDone, isAuthPending, callInitChat]);

  const send = useCallback(async (overrideText?: string, opts?: { silent?: boolean }) => {
    const text = (overrideText ?? draft).trim();
    if (!text || pending) return;

    // Two distinct kinds of system-initiated turn, deliberately NOT the same thing (parity with
    // partner/widget/src/ChatWidget.tsx):
    //  - silent (/csat): reaches the backend but renders NOTHING — the card shows its own thank-you.
    //  - internal (/thumbup, /thumbdown, /thumbclear): renders no USER bubble, but keeps the agent's
    //    acknowledgement. That ack is not an answer, so it must not carry a feedback bar itself —
    //    EXCEPT a thumbs-down that regenerates, which produces a real, rateable reply.
    const silent = opts?.silent ?? false;
    const isInternal = silent
      || text.startsWith('/thumbup') || text.startsWith('/thumbdown') || text.startsWith('/thumbclear');
    const isRegenerate = text.startsWith('/thumbdown') && text.includes('regenerate=true');
    const isSlashCmd = text.startsWith('/') && !isRegenerate;

    const sentAt = Date.now();
    const userMessageId = crypto.randomUUID();
    if (!isInternal) {
      // A fresh user-initiated question starts a new thread — reset the thumbs-down streak.
      downCountRef.current = 0;
      setDraft('');
    }
    if (!silent) setPending(true);
    if (!isInternal) {
      setMessages(prev => [...prev, { id: userMessageId, clientOperationId: userMessageId, role: 'user', text, sentAt }]);
    }

    const replyId = crypto.randomUUID();
    let accumulatedReply = '';
    if (!silent) {
      setMessages(prev => [...prev, {
        id: replyId, role: 'agent', text: '', streaming: true, sentAt,
        isSlashCommandResponse: isSlashCmd || undefined,
      }]);
    }

    // Snapshot the conversation this turn belongs to. resetChat() and an identity change both bump
    // the generation, so a callback arriving after the customer abandoned this conversation cannot
    // write to the one that replaced it. Without this a silent /csat turn — which is fire-and-forget
    // and yields at token acquisition, letting resetChat() run first — would re-apply the OLD
    // conversation id in onStart/onDone and silently continue the previous chat.
    const generation = turnGenerationRef.current;
    const owner = ownerRef.current;
    const isCurrent = () => generation === turnGenerationRef.current && owner === ownerRef.current;

    let authBearer: string | null = null;
    if (isSignedIn) {
      try {
        authBearer = await acquireApiToken();
      } catch (error) {
        // Token acquisition failed (e.g. interactive popup blocked/cancelled). Surface the error in the
        // pending reply bubble and clear pending, rather than leaving the UI stuck streaming forever.
        if (silent) return;
        setMessages(prev =>
          prev.map(message =>
            message.id === replyId
              ? { ...message, text: `${rewardsExperience.errorPrefix} ${friendlyStreamError(error as Error)}`, streaming: false, receivedAt: message.receivedAt ?? Date.now() }
              : message,
          ),
        );
        setPending(false);
        return;
      }
    }

    // Guarantee the session exists before the first stream turn. The open effect normally has it done
    // already, but the hero input opens the panel and sends in the same tick — without this the very
    // first message would race past init and let /chat/stream create the conversation implicitly (no
    // greeting, no history, identity stamped from whatever this request happened to carry).
    // callInitChat de-dupes, so the in-flight call is awaited rather than repeated.
    let effectiveConversationId = conversationId;
    if (!initChatDone || !effectiveConversationId) {
      effectiveConversationId = (await callInitChat()) ?? effectiveConversationId;
    }

    abortRef.current = sendChatMessageStream(
      {
        message: text,
        clientOperationId: userMessageId,
        conversationId: effectiveConversationId,
        authBearer,
        entryPoint: rewardsExperience.entryPoint,
        locale: requestLocale,
        homeAccountId: account?.homeAccountId,
        username: account?.username,
        displayName: account?.name,
      },
      {
        onStart: (evt: StreamEvent) => {
          if (!isCurrent()) return;
          if (evt.conversationId) {
            setConversationId(evt.conversationId);
            setPersistedConversationId(evt.conversationId, CONVERSATION_SCOPE);
          }
        },
        onToken: (chunk, metadata) => {
          if (!isCurrent()) return;
          accumulatedReply += chunk;
          const receivedAt = Date.now();
          setMessages(prev =>
            prev.map(message =>
              message.id === replyId
                ? { ...message, text: message.text + chunk, receivedAt: message.receivedAt ?? receivedAt,
                    caseDetails: readCaseDetails(metadata?.caseDetails) ?? message.caseDetails }
                : message,
            ),
          );
        },
        onDone: (evt: StreamEvent) => {
          if (!isCurrent()) return;
          if (evt.conversationId) {
            setConversationId(evt.conversationId);
            setPersistedConversationId(evt.conversationId, CONVERSATION_SCOPE);
          }
          const doneText = accumulatedReply || evt.text || evt.reply || '';
          // Parse the agent's out-of-band signals (shared with ChatWidget): resolve citations (server →
          // Sources block → inline links) and pull [SUGGESTIONS: ...] pills + the invisible [RESOLVED]
          // marker off the body so the bubble shows clean prose and the pills render as clickable choices.
          const resolved = resolveCitations(evt.citations, doneText);
          const extracted = extractSuggestedPrompts(resolved.text);
          const finalText = stripResolvedTag(extracted.text).text;
          const suggestedPrompts = extracted.suggestedPrompts.length > 0 ? extracted.suggestedPrompts : undefined;
          const citations = resolved.citations.length > 0 ? resolved.citations : undefined;
          setMessages(prev =>
            prev.map(message =>
              message.id === replyId
                ? {
                    ...message,
                    text: finalText,
                    streaming: false,
                    suggestedPrompts,
                    citations,
                    // Feedback is attached to the SERVER turn id, and only offered when the server
                    // judged this turn KB-grounded (ChatService: Citations.Count > 0 ||
                    // KnowledgeSourceUsed) — i.e. exactly the knowledge-agent answers.
                    turnId: evt.turnId ?? message.turnId,
                    feedbackEligible: evt.feedbackEligible ?? false,
                    receivedAt: message.receivedAt ?? Date.now(),
                  }
                : message,
            ),
          );
          setPending(false);
        },
        onError: (error: Error) => {
          if (!isCurrent()) return;
          if (silent) {
            // A silent turn owns no bubble, so there is nothing to annotate and nothing to unblock.
            // eslint-disable-next-line no-console
            console.error('[rewardsSupportPage] silent turn failed:', error);
            return;
          }
          setMessages(prev =>
            prev.map(message =>
              message.id === replyId
                ? { ...message, text: `${rewardsExperience.errorPrefix} ${friendlyStreamError(error)}`, streaming: false, receivedAt: message.receivedAt ?? Date.now() }
                : message,
            ),
          );
          setPending(false);
        },
      },
    );
  }, [account, acquireApiToken, callInitChat, conversationId, draft, initChatDone, isSignedIn, pending, requestLocale]);

  // Publish `send` for callbacks declared before it (completeFeedback's CSAT submit).
  useEffect(() => { sendRef.current = send; }, [send]);

  /**
   * Records (or clears) thumbs feedback for a turn, as a system-initiated slash-command turn — the
   * same contract the portal widget and the partner widget use, so the rating reaches the eval
   * pipeline instead of staying local. `rating === null` clears an existing rating.
   */
  const submitFeedback = useCallback(async (
    messageId: string,
    turnId: string,
    rating: FeedbackRating | null,
    opts?: { comment?: string; regenerate?: boolean },
  ) => {
    const mark = (patch: Partial<Message>) =>
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, ...patch } : m)));
    mark({ isFeedbackProcessing: true });
    const command =
      rating === null
        ? `/thumbclear turnId=${turnId}`
        : rating === 'up'
          ? `/thumbup turnId=${turnId}`
          : `/thumbdown turnId=${turnId}`
            + (opts?.regenerate ? ' regenerate=true' : '')
            + (opts?.comment ? ` comment="${opts.comment.replace(/"/g, "'")}"` : '');
    try {
      await send(command);
      mark({ feedback: rating, isFeedbackProcessing: false });
    } catch {
      // Roll the control back and leave any prior rating untouched.
      mark({ isFeedbackProcessing: false });
    }
  }, [send]);

  /**
   * 👍 records positive feedback and clears the 👎 streak. 👎 regenerates the answer on the first two
   * consecutive clicks and opens the reason dialog on the third. Clicking the active thumb clears it.
   */
  const handleFeedback = useCallback((message: Message, clicked: FeedbackRating, trigger: HTMLElement) => {
    const turnId = message.turnId;
    if (!turnId || message.isFeedbackProcessing) return;
    if ((message.feedback ?? null) === clicked) {
      void submitFeedback(message.id, turnId, null);
      return;
    }
    if (clicked === 'up') {
      downCountRef.current = 0;
      void submitFeedback(message.id, turnId, 'up');
      return;
    }
    if (downCountRef.current < 2) {
      downCountRef.current += 1;
      void submitFeedback(message.id, turnId, 'down', { regenerate: true });
    } else {
      reasonOpenerRef.current = trigger;
      setReasonDialog({ messageId: message.id, turnId });
    }
  }, [submitFeedback]);

  const sendText = useCallback((text: string) => void send(text), [send]);

  // Open the slide-out panel and (optionally) fire the first message. Used by the hero input and pills.
  const openPanelWith = useCallback((text?: string, opener?: HTMLElement) => {
    if (!panelOpenRef.current) {
      openerRef.current = opener
        ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    }
    setPanelOpen(true);
    if (text && text.trim()) void send(text.trim());
  }, [send]);

  const closePanel = useCallback(() => setPanelOpen(false), []);
  useRewardsDialog(panelRef, panelOpen, inputRef, { openerRef, lockBody: true });

  const onHeroSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = heroDraft.trim();
    const active = document.activeElement;
    const submitter = event.nativeEvent instanceof SubmitEvent ? event.nativeEvent.submitter : null;
    const opener = active instanceof HTMLElement && event.currentTarget.contains(active)
      ? active : submitter instanceof HTMLElement ? submitter : undefined;
    if (!text) { openPanelWith(undefined, opener); return; }
    setHeroDraft('');
    openPanelWith(text, opener);
  }, [heroDraft, openPanelWith]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void send();
    }
  };

  // Live-filtered as the user types in the hero "Ask" box: narrows QUICK_PROMPTS to whatever
  // matches the in-progress text, so suggestions react on every keystroke instead of sitting
  // static. Falls back to the full list once the box is empty again.
  const heroDraftQuery = heroDraft.trim().toLowerCase();
  const heroSuggestions = heroDraftQuery
    ? QUICK_PROMPTS.filter(p => p.label.toLowerCase().includes(heroDraftQuery))
    : QUICK_PROMPTS;

  const showQuickPrompts = messages.length === 1 && messages[0].id === 'welcome';
  const channelLabel = 'AI Agent';
  // Choice pills belong to the LAST message only: the moment the user answers (clicks a pill or
  // types), a user message becomes the tail and the pills retire immediately — never stale/duplicated.
  const tailMessage = messages[messages.length - 1];
  const latestSuggestedPrompts =
    tailMessage && tailMessage.role === 'agent'
      ? tailMessage.suggestedPrompts ?? []
      : [];

  return (
    <div className="rewards-support-page" style={styles.shell}>
      <header className="rewards-topbar" style={styles.topbar}>
        <a className="rewards-brand" style={styles.brand} href="#" onClick={(e) => e.preventDefault()}>
          <img style={styles.brandImg} src={rewardsExperience.brandLogo} alt="Microsoft Rewards logo" />
        </a>
        <nav className="rewards-nav" style={styles.nav} aria-label="Microsoft Rewards">
          {rewardsExperience.navItems.map(item => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              style={item.label === rewardsExperience.navActive ? styles.navLinkActive : styles.navLink}
            >
              {item.label}
            </a>
          ))}
        </nav>
        {isSignedIn ? (
          <span className="rewards-profile" style={styles.profileLink} title={account?.username}>{account?.name ?? 'Account'}</span>
        ) : (
          <button className="rewards-profile" type="button" style={styles.profileLinkButton} onClick={() => signIn()}>Sign in</button>
        )}
      </header>

      <main style={styles.main}>
        <section className="rewards-hero" style={styles.hero}>
          <div style={styles.heroCopy}>
            <p style={styles.eyebrow}>{rewardsExperience.pageEyebrow}</p>
            <h1 style={styles.heroTitle}>{rewardsExperience.pageHeadline}</h1>
            <p style={styles.subtitle}>{rewardsExperience.pageSubtitle}</p>
            {!isSignedIn ? (
              <button type="button" style={styles.primaryButton} onClick={() => signIn()}>
                {rewardsExperience.signInCtaLabel}
              </button>
            ) : (
              <div style={styles.assistantEntry} aria-live="polite">
                <form className="rewards-chatbox" style={styles.chatBox} onSubmit={onHeroSubmit}>
                  <input
                    style={styles.heroInput}
                    className="rewards-support-input"
                    type="text"
                    value={heroDraft}
                    onChange={e => setHeroDraft(e.target.value)}
                    placeholder={rewardsExperience.heroInputPlaceholder}
                    autoComplete="off"
                    aria-label={rewardsExperience.heroInputPlaceholder}
                  />
                  <button style={styles.heroSendButton} type="submit">{rewardsExperience.heroAskLabel}</button>
                </form>
                {heroSuggestions.length > 0 && (
                  <div style={styles.pills} aria-label="Suggested prompts">
                    {heroSuggestions.map(p => (
                      <button key={p.label} type="button" style={styles.pill} onClick={event => openPanelWith(p.prompt, event.currentTarget)}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="rewards-hero-art" style={styles.heroArt} aria-hidden="true" />
        </section>

        <section className="rewards-section" style={styles.section}>
          <h2 style={styles.sectionTitle}>{rewardsExperience.sectionTitle}</h2>
          <p style={styles.sectionIntro}>{rewardsExperience.sectionIntro}</p>
          <div className="rewards-pillars" style={styles.pillars}>
            {rewardsExperience.pillars.map(pillar => (
              <article key={pillar.title} style={styles.pillar}>
                <img style={styles.pillarImg} src={pillar.image} alt={pillar.title} />
                <div style={styles.pillarBody}>
                  <h3 style={styles.pillarTitle}>{pillar.title}</h3>
                  <p style={styles.pillarText}>{pillar.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="rewards-products" style={styles.products}>
          <h2 style={styles.sectionTitle}>{rewardsExperience.productsTitle}</h2>
          <p style={styles.sectionIntro}>{rewardsExperience.productsIntro}</p>
          <div style={styles.productRow}>
            {rewardsExperience.products.map(product => (
              <img key={product.name} style={styles.productImg} src={product.image} alt={product.name} />
            ))}
          </div>
        </section>
      </main>

      <dialog ref={panelRef} className="rewards-chat-dialog" style={styles.chatPanel(panelOpen)}
        aria-label={rewardsExperience.panelTitle} hidden={!panelOpen} tabIndex={-1}
        onCancel={event => { event.preventDefault(); closePanel(); }}
        onClick={event => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right
            || event.clientY < bounds.top || event.clientY > bounds.bottom) closePanel();
        }}>
        <div className="rewards-chat-header" style={styles.panelHeader}>
          <span style={styles.statusDot} aria-hidden="true" />
          <h2 style={styles.panelTitle}>{rewardsExperience.panelTitle}</h2>
          <span style={styles.badge}>{channelLabel}</span>
          <button ref={newChatRef} type="button" onClick={requestNewChat} style={styles.newChat}>+ New</button>
          <button type="button" onClick={closePanel} style={styles.closeButton} aria-label="Close chat">×</button>
        </div>

        <div ref={scrollRef} style={styles.messages}>
          {messages.map(message => (
            <div key={message.id} style={styles.row(message.role)}>
              <div style={styles.bubble(message.role)}>
                {message.role === 'agent' ? (
                  message.text
                    ? (
                      <>
                        {message.text && (
                          <CaseDetailsMessage text={stripSignalsForDisplay(message.text)}
                            caseDetails={message.caseDetails} renderText={text => (
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>
                            )} />
                        )}
                        {message.streaming && <span style={styles.cursorBlink}>▊</span>}
                        {!message.streaming && message.citations && message.citations.length > 0 && (
                          <CitationList citations={message.citations} />
                        )}
                        {message.sentAt && message.receivedAt && (
                          <div style={styles.responseTime}>
                            <span style={styles.responseTimeLabel}>Time:</span>
                            <span style={styles.responseTimeValue}>{formatLatency(message.receivedAt - message.sentAt)}</span>
                          </div>
                        )}
                        {message.text && !message.streaming && (
                          <div style={styles.copyBar}>
                            <button
                              type="button"
                              aria-label={copiedMsgId === message.id ? 'Copied' : 'Copy'}
                              title={copiedMsgId === message.id ? 'Copied' : 'Copy'}
                              onClick={() => handleCopy(message.id)}
                              style={{
                                ...styles.copyButton,
                                ...(copiedMsgId === message.id ? styles.copyButtonActive : {}),
                              }}
                            >
                              {copiedMsgId === message.id ? (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                              )}
                            </button>
                            {/* Thumbs sit beside Copy, but are gated independently: Copy is useful on
                                every reply, while a rating is only meaningful on a KB-grounded answer
                                (server-set feedbackEligible) and needs the server turn id. A slash
                                command's bare acknowledgement is never rateable. */}
                            {!message.isSlashCommandResponse && message.feedbackEligible && message.turnId && (
                              <>
                                <button
                                  type="button"
                                  aria-label={EN_STRINGS.feedback.helpful}
                                  aria-pressed={message.feedback === 'up'}
                                  title={EN_STRINGS.feedback.helpful}
                                  disabled={message.isFeedbackProcessing}
                                  onClick={event => handleFeedback(message, 'up', event.currentTarget)}
                                  style={{
                                    ...styles.copyButton,
                                    ...(message.feedback === 'up' ? styles.copyButtonActive : {}),
                                    ...(message.isFeedbackProcessing ? styles.feedbackButtonDisabled : {}),
                                  }}
                                >
                                  👍
                                </button>
                                <button
                                  type="button"
                                  aria-label={EN_STRINGS.feedback.notHelpful}
                                  aria-pressed={message.feedback === 'down'}
                                  title={EN_STRINGS.feedback.notHelpful}
                                  disabled={message.isFeedbackProcessing}
                                  onClick={event => handleFeedback(message, 'down', event.currentTarget)}
                                  style={{
                                    ...styles.copyButton,
                                    ...(message.feedback === 'down' ? styles.copyButtonActive : {}),
                                    ...(message.isFeedbackProcessing ? styles.feedbackButtonDisabled : {}),
                                  }}
                                >
                                  👎
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )
                    : pending ? (
                      <TypingDots />
                    ) : null
                ) : (
                  message.text
                )}
              </div>
            </div>
          ))}
          {showQuickPrompts && (
            <div style={styles.quickPrompts}>
              {QUICK_PROMPTS.map(quickPrompt => (
                <button
                  key={quickPrompt.label}
                  type="button"
                  style={styles.quickButton}
                  onClick={() => sendText(quickPrompt.prompt)}
                >
                  {quickPrompt.label}
                </button>
              ))}
            </div>
          )}
          {latestSuggestedPrompts.length > 0 && !pending && (
            <div style={styles.quickPrompts}>
              {latestSuggestedPrompts.map(prompt => (
                <button
                  key={prompt}
                  type="button"
                  style={styles.quickButton}
                  onClick={() => sendText(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>

        {feedbackReason && (
          <FeedbackCard
            rating={feedbackRating}
            onRate={(rating) => completeFeedback(rating)}
            onSkip={() => completeFeedback()}
            onHover={setFeedbackRating}
          />
        )}

        {reasonDialog && (
          <FeedbackReasonDialog
            openerRef={reasonOpenerRef}
            onCancel={() => setReasonDialog(null)}
            onSubmit={(comment) => {
              const { messageId, turnId } = reasonDialog;
              setReasonDialog(null);
              downCountRef.current = 0;
              void submitFeedback(messageId, turnId, 'down', { comment });
            }}
          />
        )}

        <div style={styles.composer}>
          <input
            ref={inputRef}
            className="rewards-support-input"
            aria-label="Ask the Microsoft Rewards Assistant"
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={rewardsExperience.panelInputPlaceholder}
            style={styles.input}
            readOnly={pending}
            aria-busy={pending}
          />
          <button type="button" onClick={() => void send()} disabled={pending || !draft.trim()} style={styles.send}>
            {rewardsExperience.sendButtonLabel}
          </button>
        </div>
        <div style={styles.disclaimer}>
          AI-generated content may be incorrect. Verify important information independently.
        </div>
        <footer style={styles.privacyFooter}>
          © {new Date().getFullYear()} - Microsoft Corporation |{' '}
          <a
            href="https://privacy.microsoft.com/privacystatement"
            target="_blank"
            rel="noreferrer"
            style={styles.privacyLink}
          >
            Privacy
          </a>
        </footer>
      </dialog>
    </div>
  );
}

/** Renders the agent's grounding sources behind a collapsible "Sources" hyperlink (parity with ChatWidget). */
function CitationList({ citations }: { citations: Citation[] }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, color: '#444', lineHeight: 1.5 }}>
        Learn more about where this information comes from in{' '}
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          title={expanded ? 'Hide sources' : 'Show sources'}
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: rewardsTheme.accent, textDecoration: 'underline', font: 'inherit',
          }}
        >
          Sources
        </button>.
      </div>
      {expanded && (
        <ol style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
          {citations.map((c, i) => (
            <li key={i} style={{ marginBottom: 2 }}>
              {c.url ? (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={c.snippet ?? c.url}
                  style={{ color: rewardsTheme.accent, textDecoration: 'none', wordBreak: 'break-word' }}
                >
                  {c.title || c.url}
                </a>
              ) : (
                <span title={c.snippet ?? undefined} style={{ color: '#374151' }}>{c.title}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function TypingDots() {
  return (
    <span style={styles.typingDots} role="status" aria-label="Agent is typing">
      <span style={{ ...styles.typingDot, animationDelay: '0ms' }} />
      <span style={{ ...styles.typingDot, animationDelay: '200ms' }} />
      <span style={{ ...styles.typingDot, animationDelay: '400ms' }} />
    </span>
  );
}

function FeedbackCard({
  rating,
  onRate,
  onSkip,
  onHover,
}: {
  rating: number;
  onRate: (rating: number) => void;
  onSkip: () => void;
  onHover: (rating: number) => void;
}) {
  const title = 'Before starting a new chat, how was this conversation?';
  const sectionRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    sectionRef.current?.focus();
    sectionRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);

  return (
    <section ref={sectionRef} className="rewards-feedback-card" style={styles.feedbackCard}
      role="region" aria-label="Chat feedback" tabIndex={-1}>
      <div>
        <h3 style={styles.feedbackTitle}>{title}</h3>
        <div style={styles.feedbackSubtitle}>Your rating helps improve Microsoft Rewards support.</div>
      </div>
      <div className="rewards-feedback-actions" style={styles.feedbackActions}>
        <div style={styles.starRow} onMouseLeave={() => onHover(0)}>
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              aria-label={`${star} star${star === 1 ? '' : 's'}`}
              style={styles.starButton(star <= rating)}
              onMouseEnter={() => onHover(star)}
              onFocus={() => onHover(star)}
              onClick={() => onRate(star)}
            >
              ★
            </button>
          ))}
        </div>
        <button type="button" onClick={onSkip} style={styles.skipFeedbackButton}>
          Skip
        </button>
      </div>
    </section>
  );
}

// The array holds KEYS so the checkbox label can be localized, but the reason SUBMITTED with the
// feedback is always EN_STRINGS.reason[key] — the English display text — so analytics stay
// comparable across locales. Same mapping and separators as partner/widget's FeedbackReasonDialog,
// so an identical selection produces an identical comment on every surface (ADR-0006).
const NEGATIVE_FEEDBACK_REASONS = [
  'incorrectAnswer',
  'notHelpful',
  'tooLong',
  'tooShort',
  'outdated',
  'offensive',
  'other',
] as const;

/** Negative-feedback reason dialog ("What went wrong?"). Checkboxes + optional free-text detail. */
function FeedbackReasonDialog({
  onSubmit,
  onCancel,
  openerRef,
}: {
  onSubmit: (comment: string) => void;
  onCancel: () => void;
  openerRef: React.RefObject<HTMLElement | null>;
}) {
  const [selected, setSelected] = useState<Record<string, boolean>>({ other: true });
  const [detail, setDetail] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useRewardsDialog(dialogRef, true, textareaRef, { openerRef });

  const submit = () => {
    const reasons = NEGATIVE_FEEDBACK_REASONS.filter(r => selected[r]).map(r => EN_STRINGS.reason[r]);
    const parts: string[] = [];
    if (reasons.length > 0) parts.push(reasons.join(', '));
    if (detail.trim()) parts.push(detail.trim());
    onSubmit(parts.join(' — '));
  };

  return (
    <dialog ref={dialogRef} className="rewards-reason-dialog" style={styles.reasonDialog}
      aria-label={EN_STRINGS.reason.dialogLabel} tabIndex={-1}
      onCancel={event => { event.preventDefault(); event.stopPropagation(); onCancel(); }}>
        <h2 style={styles.reasonTitle}>{EN_STRINGS.reason.title}</h2>
        <div style={styles.reasonOptions}>
          {NEGATIVE_FEEDBACK_REASONS.map(reason => (
            <label key={reason} style={styles.reasonOption}>
              <input
                type="checkbox"
                checked={!!selected[reason]}
                onChange={() => setSelected(prev => ({ ...prev, [reason]: !prev[reason] }))}
              />
              {EN_STRINGS.reason[reason]}
            </label>
          ))}
        </div>
        <textarea
          ref={textareaRef}
          value={detail}
          onChange={event => setDetail(event.target.value)}
          placeholder={EN_STRINGS.reason.detailPlaceholder}
          style={styles.reasonTextarea}
        />
        <div style={styles.reasonActions}>
          <button type="button" onClick={onCancel} style={styles.skipFeedbackButton}>
            {EN_STRINGS.reason.cancel}
          </button>
          <button type="button" onClick={submit} style={styles.reasonSubmit}>
            {EN_STRINGS.reason.submit}
          </button>
          </div>
      </dialog>
  );
}

const markdownComponents = {
  a: ({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) =>
    href
      ? (
        <a {...props} href={href} target="_blank" rel="noopener noreferrer" style={styles.link}>
          {children}
        </a>
      )
      : (
        // No destination: render as plain text rather than an empty anchor (avoids anchor-no-href).
        <span>{children}</span>
      ),
  p: ({ children }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p style={styles.markdownParagraph}>{children}</p>
  ),
  ul: ({ children }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul style={styles.markdownList}>{children}</ul>
  ),
  ol: ({ children }: React.OlHTMLAttributes<HTMLOListElement>) => (
    <ol style={styles.markdownList}>{children}</ol>
  ),
  li: ({ children }: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li style={styles.markdownListItem}>{children}</li>
  ),
};

function formatLatency(milliseconds: number): string {
  if (milliseconds < 1000) return `${Math.max(0, Math.round(milliseconds))}ms`;
  return `${(milliseconds / 1000).toFixed(1)}s`;
}

const rewardsTheme = rewardsExperience.theme;

const styles = {
  shell: {
    minHeight: '100vh',
    background: rewardsTheme.pageBackground,
    fontFamily: 'Segoe UI, Aptos, Calibri, system-ui, sans-serif',
    color: rewardsTheme.text,
  } as React.CSSProperties,
  topbar: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    gap: 24,
    padding: '12px 32px',
    background: rewardsTheme.pageBackgroundElevated,
    borderBottom: `1px solid ${rewardsTheme.border}`,
  } as React.CSSProperties,
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 220,
  } as React.CSSProperties,
  brandImg: {
    display: 'block',
    height: 18,
    maxWidth: 165,
    objectFit: 'contain',
  } as React.CSSProperties,
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    overflowX: 'auto',
  } as React.CSSProperties,
  navLink: {
    borderRadius: 10,
    color: rewardsTheme.textMuted,
    padding: '10px 14px',
    whiteSpace: 'nowrap',
    fontSize: 15,
    cursor: 'pointer',
  } as React.CSSProperties,
  navLinkActive: {
    borderRadius: 10,
    background: rewardsTheme.accentSoft,
    color: rewardsTheme.text,
    padding: '10px 14px',
    whiteSpace: 'nowrap',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
  profileLink: {
    color: rewardsTheme.link,
    fontSize: 15,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  profileLinkButton: {
    border: 0,
    background: 'transparent',
    color: rewardsTheme.link,
    font: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    padding: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  main: {
    overflow: 'hidden',
  } as React.CSSProperties,
  hero: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 640px) minmax(280px, 1fr)',
    gap: 32,
    alignItems: 'center',
    maxWidth: 1280,
    margin: '0 auto',
    minHeight: 600,
    padding: '72px 32px 64px',
    background: `${rewardsTheme.pageBackgroundElevated} url("${rewardsExperience.heroImage}") right center / min(52vw, 660px) auto no-repeat`,
    borderBottom: `1px solid ${rewardsTheme.border}`,
    borderRadius: '0 0 16px 16px',
  } as React.CSSProperties,
  heroCopy: {
    maxWidth: 640,
  } as React.CSSProperties,
  eyebrow: {
    margin: '0 0 16px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: 15,
    fontWeight: 600,
    color: rewardsTheme.textMuted,
  } as React.CSSProperties,
  heroTitle: {
    margin: '0 0 24px',
    fontSize: 'clamp(30px, 3.6vw, 44px)',
    lineHeight: 1.06,
    fontWeight: 650,
    letterSpacing: '-0.035em',
  } as React.CSSProperties,
  subtitle: {
    margin: '0 0 32px',
    fontSize: 20,
    lineHeight: 1.45,
    color: rewardsTheme.textMuted,
    maxWidth: 560,
  } as React.CSSProperties,
  primaryButton: {
    alignSelf: 'flex-start',
    border: `1px solid ${rewardsTheme.text}`,
    borderRadius: 10,
    padding: '14px 22px',
    background: rewardsTheme.text,
    color: rewardsTheme.surface,
    fontWeight: 650,
    fontSize: 15,
    cursor: 'pointer',
  } as React.CSSProperties,
  assistantEntry: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 8,
    maxWidth: 820,
  } as React.CSSProperties,
  chatBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 68,
    background: rewardsTheme.surface,
    border: `1px solid ${rewardsTheme.borderStrong}`,
    borderRadius: 16,
    padding: '12px 14px 12px 20px',
    boxShadow: `0 0 2px ${rewardsTheme.border}, 0 1px 2px ${rewardsTheme.border}`,
  } as React.CSSProperties,
  heroInput: {
    flex: 1,
    minHeight: 42,
    border: 0,
    background: 'transparent',
    font: 'inherit',
    fontSize: 16,
    color: rewardsTheme.text,
  } as React.CSSProperties,
  heroSendButton: {
    border: 0,
    borderRadius: 10,
    minWidth: 44,
    padding: '10px 14px',
    background: rewardsTheme.text,
    color: rewardsTheme.surface,
    fontWeight: 650,
    fontSize: 15,
    cursor: 'pointer',
  } as React.CSSProperties,
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 14,
  } as React.CSSProperties,
  pill: {
    padding: '10px 14px',
    background: rewardsTheme.surface,
    border: `1px solid ${rewardsTheme.border}`,
    borderRadius: 999,
    color: rewardsTheme.text,
    font: 'inherit',
    fontSize: 14,
    cursor: 'pointer',
  } as React.CSSProperties,
  heroArt: {
    minHeight: 420,
  } as React.CSSProperties,
  section: {
    maxWidth: 1180,
    margin: '0 auto',
    padding: '40px 32px 72px',
  } as React.CSSProperties,
  sectionTitle: {
    margin: '0 0 12px',
    fontSize: 'clamp(26px, 3vw, 36px)',
    fontWeight: 700,
    letterSpacing: '-0.03em',
    textAlign: 'center',
  } as React.CSSProperties,
  sectionIntro: {
    margin: '0 auto 32px',
    fontSize: 18,
    lineHeight: 1.45,
    color: rewardsTheme.textMuted,
    maxWidth: 720,
    textAlign: 'center',
  } as React.CSSProperties,
  pillars: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  } as React.CSSProperties,
  pillar: {
    background: rewardsTheme.surface,
    border: `1px solid ${rewardsTheme.border}`,
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: `0 0 2px ${rewardsTheme.border}, 0 1px 2px ${rewardsTheme.border}`,
  } as React.CSSProperties,
  pillarImg: {
    display: 'block',
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover',
  } as React.CSSProperties,
  pillarBody: {
    padding: 22,
  } as React.CSSProperties,
  pillarTitle: {
    margin: '0 0 10px',
    fontSize: 22,
    fontWeight: 700,
    color: rewardsTheme.text,
  } as React.CSSProperties,
  pillarText: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.5,
    color: rewardsTheme.textMuted,
  } as React.CSSProperties,
  products: {
    background: rewardsTheme.pageBackgroundElevated,
    borderTop: `1px solid ${rewardsTheme.border}`,
    padding: '56px 32px',
    textAlign: 'center',
  } as React.CSSProperties,
  productRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 28,
    marginTop: 24,
    flexWrap: 'wrap',
  } as React.CSSProperties,
  productImg: {
    height: 38,
    maxWidth: 120,
  } as React.CSSProperties,
  chatPanel: (open: boolean): React.CSSProperties => ({
    position: 'fixed',
    top: '50%',
    left: '50%',
    height: 'min(820px, 90vh)',
    width: 'min(880px, 94vw)',
    background: rewardsTheme.surface,
    border: `1px solid ${rewardsTheme.border}`,
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: open ? rewardsTheme.shadow : 'none',
    display: open ? 'flex' : 'none',
    flexDirection: 'column',
    transform: open ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.96)',
    opacity: open ? 1 : 0,
    pointerEvents: open ? 'auto' : 'none',
    transition: 'transform 0.24s ease, opacity 0.2s ease',
    zIndex: 50,
    margin: 0,
    padding: 0,
    maxWidth: 'none',
    maxHeight: 'none',
    color: rewardsTheme.text,
  }),
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 16px',
    background: rewardsTheme.accent,
    color: rewardsTheme.accentFg,
  } as React.CSSProperties,
  statusDot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    background: '#6ee7a8',
    boxShadow: '0 0 0 3px rgba(110,231,168,0.3)',
  } as React.CSSProperties,
  panelTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.2,
  } as React.CSSProperties,
  badge: {
    borderRadius: 10,
    padding: '2px 8px',
    background: 'rgba(0,0,0,0.22)',
    fontSize: 11,
  } as React.CSSProperties,
  newChat: {
    marginLeft: 'auto',
    padding: '5px 12px',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: 6,
    background: 'transparent',
    color: '#fff',
    font: 'inherit',
    fontSize: 12,
    cursor: 'pointer',
  } as React.CSSProperties,
  closeButton: {
    border: 0,
    background: 'transparent',
    color: '#fff',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px',
  } as React.CSSProperties,
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: 18,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  } as React.CSSProperties,
  row: (role: Role): React.CSSProperties => ({
    display: 'flex',
    maxWidth: '88%',
    alignSelf: role === 'user' ? 'flex-end' : role === 'system' ? 'center' : 'flex-start',
  }),
  bubble: (role: Role): React.CSSProperties => ({
    width: '100%',
    padding: role === 'system' ? '6px 12px' : '11px 14px',
    borderRadius: role === 'system'
      ? 16
      : role === 'user'
        ? `${12}px ${12}px ${4}px ${12}px`
        : `${12}px ${12}px ${12}px ${4}px`,
    background: role === 'system'
      ? '#e8e8e8'
      : role === 'user'
        ? rewardsTheme.accent
        : rewardsTheme.surfaceSoft,
    border: 'none',
    color: role === 'system' ? '#666' : role === 'user' ? rewardsTheme.accentFg : '#1a1a1a',
    boxShadow: role === 'system' ? 'none' : '0 1px 3px rgba(0,0,0,0.10)',
    lineHeight: 1.6,
    fontSize: role === 'system' ? 12 : 14,
    wordBreak: 'break-word',
    whiteSpace: role === 'user' || role === 'system' ? 'pre-wrap' : undefined,
  }),
  composer: {
    display: 'flex',
    gap: 10,
    padding: '14px 16px',
    borderTop: `1px solid ${rewardsTheme.border}`,
    background: rewardsTheme.surface,
  } as React.CSSProperties,
  input: {
    flex: 1,
    border: `1px solid ${rewardsTheme.border}`,
    borderRadius: 999,
    padding: '11px 16px',
    font: 'inherit',
    fontSize: 14,
  } as React.CSSProperties,
  send: {
    border: 0,
    borderRadius: 999,
    padding: '11px 20px',
    background: rewardsTheme.accent,
    color: rewardsTheme.accentFg,
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  } as React.CSSProperties,
  feedbackCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '14px 16px',
    background: rewardsTheme.surface,
    borderTop: `1px solid ${rewardsTheme.border}`,
    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
  } as React.CSSProperties,
  feedbackTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: rewardsTheme.text,
  } as React.CSSProperties,
  feedbackSubtitle: {
    marginTop: 2,
    fontSize: 12,
    color: rewardsTheme.textMuted,
  } as React.CSSProperties,
  feedbackActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  } as React.CSSProperties,
  starRow: {
    display: 'flex',
    gap: 2,
  } as React.CSSProperties,
  starButton: (active: boolean): React.CSSProperties => ({
    border: 0,
    background: 'transparent',
    color: active ? '#f5b301' : '#c8c8c8',
    cursor: 'pointer',
    fontSize: 24,
    lineHeight: 1,
    padding: '2px 3px',
  }),
  skipFeedbackButton: {
    border: `1px solid ${rewardsTheme.border}`,
    borderRadius: 8,
    background: rewardsTheme.surface,
    color: rewardsTheme.textMuted,
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 13,
    padding: '7px 12px',
  } as React.CSSProperties,
  link: {
    color: rewardsTheme.link,
    textDecoration: 'none',
  } as React.CSSProperties,
  markdownParagraph: {
    margin: '2px 0',
  } as React.CSSProperties,
  markdownList: {
    margin: '4px 0',
    paddingLeft: 22,
  } as React.CSSProperties,
  markdownListItem: {
    margin: '2px 0',
  } as React.CSSProperties,
  typingDots: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 0',
  } as React.CSSProperties,
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#999',
    display: 'inline-block',
    animation: 'cmsp-chat-bounce 1.4s infinite',
  } as React.CSSProperties,
  cursorBlink: {
    display: 'inline-block',
    marginLeft: 2,
    color: rewardsTheme.accent,
  } as React.CSSProperties,
  responseTime: {
    display: 'inline-flex',
    gap: 3,
    marginTop: 6,
    paddingTop: 6,
    borderTop: '1px solid #f0f0f0',
    color: '#888',
    fontSize: 11,
  } as React.CSSProperties,
  responseTimeLabel: {
    color: '#737373',
  } as React.CSSProperties,
  responseTimeValue: {
    color: '#555',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  copyBar: {
    display: 'flex',
    gap: 4,
    marginTop: 6,
  } as React.CSSProperties,
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 6,
    padding: '2px 6px',
    cursor: 'pointer',
    color: '#555',
    opacity: 0.6,
    transition: 'opacity 0.15s, background 0.15s, border-color 0.15s',
  } as React.CSSProperties,
  copyButtonActive: {
    opacity: 1,
    background: rewardsTheme.accentSoft,
    borderColor: rewardsTheme.accent,
    color: rewardsTheme.accent,
  } as React.CSSProperties,
  feedbackButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  } as React.CSSProperties,
  reasonDialog: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: 'calc(100% - 32px)',
    maxWidth: 360,
    padding: 16,
    borderRadius: 12,
    background: rewardsTheme.surface,
    border: `1px solid ${rewardsTheme.border}`,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    maxHeight: 'calc(100dvh - 32px)',
    overflowY: 'auto',
    margin: 'auto',
  } as React.CSSProperties,
  reasonTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 14,
    color: rewardsTheme.text,
  } as React.CSSProperties,
  reasonOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  } as React.CSSProperties,
  reasonOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: rewardsTheme.text,
    cursor: 'pointer',
  } as React.CSSProperties,
  reasonTextarea: {
    border: `1px solid ${rewardsTheme.border}`,
    borderRadius: 8,
    padding: '8px 10px',
    font: 'inherit',
    fontSize: 13,
    minHeight: 64,
    resize: 'vertical',
    color: rewardsTheme.text,
    background: rewardsTheme.surface,
  } as React.CSSProperties,
  reasonActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  } as React.CSSProperties,
  reasonSubmit: {
    border: 'none',
    borderRadius: 8,
    background: rewardsTheme.accent,
    color: rewardsTheme.accentFg,
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    padding: '7px 12px',
  } as React.CSSProperties,
  quickPrompts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignSelf: 'flex-start',
    padding: '4px 0',
  } as React.CSSProperties,
  quickButton: {
    padding: '8px 14px',
    background: rewardsTheme.accentSoft,
    border: `1px solid ${rewardsTheme.border}`,
    borderRadius: 20,
    color: rewardsTheme.accent,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,
  disclaimer: {
    padding: '6px 16px',
    background: rewardsTheme.pageBackground,
    borderTop: `1px solid ${rewardsTheme.border}`,
    color: rewardsTheme.textSoft,
    fontSize: 11,
    textAlign: 'center',
  } as React.CSSProperties,
  // RAI requirement (OneRAI release assessment): disclose what data is collected and link to the
  // Microsoft privacy statement, matching the footer already shipped on the Teams support agent page
  // (AB#6732286). Themed with the panel's own header color (rewardsTheme.accent/accentFg) rather than
  // Teams' purple, the same per-agent-brand convention Teams itself used (commit 9b2c34ad).
  privacyFooter: {
    padding: '12px 16px',
    background: rewardsTheme.accent,
    color: rewardsTheme.accentFg,
    fontSize: 12,
    textAlign: 'center',
  } as React.CSSProperties,
  privacyLink: {
    color: rewardsTheme.accentFg,
    textDecoration: 'underline',
  } as React.CSSProperties,
};
